import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ServiceCardDTO } from '@newflix/shared';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { Card } from '../components/Card.tsx';

interface CategoryPageProps {
  slug: string;
  favs: string[];
  onFav: (id: string) => void;
  onOpen: (id: string) => void;
}

type Sort = 'popular' | 'recent' | 'rating';
const SORTS: Array<[Sort, string]> = [
  ['popular', '인기순'],
  ['recent', '최신순'],
  ['rating', '평점순'],
];

export function CategoryPage({ slug, favs, onFav, onOpen }: CategoryPageProps) {
  const decoded = decodeURIComponent(slug);
  const [sort, setSort] = useState<Sort>('popular');

  const q = useQuery({
    queryKey: qk.categories.detail(decoded),
    queryFn: () => api.getCategory(decoded),
  });
  const items: ServiceCardDTO[] = q.data?.items ?? [];
  const cat = q.data?.category;

  const list = useMemo<ServiceCardDTO[]>(() => {
    const arr = [...items];
    if (sort === 'popular') return arr.sort((a, b) => b.upvotes - a.upvotes);
    if (sort === 'recent') return arr.sort((a, b) => a.daysSincePublished - b.daysSincePublished);
    return arr.sort((a, b) => b.rating - a.rating);
  }, [items, sort]);

  const color = cat?.color ?? items[0]?.brandColor ?? 'var(--accent)';
  const name = cat?.name ?? decoded;

  if (q.isLoading) {
    return (
      <main className="nf-page" style={{ padding: '160px 56px', color: 'var(--fg-muted)' }}>
        불러오는 중…
      </main>
    );
  }
  if (q.error) {
    return (
      <main className="nf-page" style={{ padding: '160px 56px', color: 'var(--fg-muted)' }}>
        카테고리를 찾을 수 없어요.
      </main>
    );
  }

  return (
    <main className="nf-page nf-cat">
      <div className="nf-fav-head" style={{ paddingTop: 120 }}>
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color,
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            <span style={{ width: 24, height: 2, background: color }} />
            카테고리
          </div>
          <h1>{name}</h1>
          <div className="sub">{list.length}개의 서비스</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {SORTS.map(([k, l]) => (
            <button
              key={k}
              className={`nf-btn ${sort === k ? 'accent' : 'ghost'}`}
              style={{ height: 36, padding: '0 14px', fontSize: 13 }}
              onClick={() => setSort(k)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="nf-grid">
        {list.map((s) => (
          <Card key={s.slug} service={s} isFav={favs.includes(s.slug)} onFav={onFav} onOpen={onOpen} />
        ))}
      </div>
    </main>
  );
}
